import React, { useState, useRef, useMemo, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	TextField,
	Checkbox,
	Chip,
	Button,
	Stepper,
	Step,
	StepLabel,
	ToggleButton,
	ToggleButtonGroup,
	Autocomplete,
	Alert,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";

import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
// adjust import path for activeCodes if needed
import { activeCodes } from "../../utils/functions";

const QuickAssignDialog = ({
	open,
	onClose,
	onSave,
	equipment = [],
	vehicles = [],
	employees = [],
	codes = {},
}) => {
	// ── state ──────────────────────────────────────────────────────────────
	const [step, setStep] = useState(0); // 0 = Select Items, 1 = Set Destination
	const [mode, setMode] = useState("list"); // "list" | "scan"
	const [queue, setQueue] = useState([]);

	const [assignStatus, setAssignStatus] = useState("");
	const [empMode, setEmpMode] = useState("keep"); // keep | unassign | assign
	const [vehMode, setVehMode] = useState("keep");
	const [locMode, setLocMode] = useState("keep"); // keep | clear | set
	const [assign, setAssign] = useState({
		employeeID: "",
		vehicleID: "",
		location: "",
	});

	const [listSearch, setListSearch] = useState("");
	const [filterEmpID, setFilterEmpID] = useState("");
	const [filterVehID, setFilterVehID] = useState("");
	const [filterLoc, setFilterLoc] = useState("");
	const [filterStatus, setFilterStatus] = useState("");

	const [scanInput, setScanInput] = useState("");
	const [scanMsg, setScanMsg] = useState({ text: "", ok: false });
	const [scannerActive, setScannerActive] = useState(false);
	const [confirmClose, setConfirmClose] = useState(false);

	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const scanInputRef = useRef(null);

	// ── derived data ───────────────────────────────────────────────────────
	const uniqueLocations = useMemo(
		() =>
			[
				...new Set(equipment.map((e) => e.location).filter(Boolean)),
			].sort(),
		[equipment],
	);

	const filteredEquip = useMemo(() => {
		const kws = listSearch
			.trim()
			.split(/\s+/)
			.filter(Boolean)
			.map((k) => k.toLowerCase());

		return equipment.filter((eq) => {
			if (
				kws.length &&
				!kws.every(
					(k) =>
						(eq.serialNumber || "").toLowerCase().includes(k) ||
						(eq.assetTag || "").toLowerCase().includes(k) ||
						(eq.propertyNumber || "").toLowerCase().includes(k) ||
						(eq.type || "").toLowerCase().includes(k) ||
						(eq.brand || "").toLowerCase().includes(k) ||
						(eq.model || "").toLowerCase().includes(k),
				)
			)
				return false;

			if (filterEmpID) {
				if (filterEmpID === "0") {
					if (eq.assignedOfficerID) return false;
				} else if (String(eq.assignedOfficerID || "") !== filterEmpID)
					return false;
			}
			if (filterVehID) {
				if (filterVehID === "0") {
					if (eq.assignedVehicleID) return false;
				} else if (String(eq.assignedVehicleID || "") !== filterVehID)
					return false;
			}
			if (filterLoc) {
				if (filterLoc === "_none_" && eq.location) return false;
				if (filterLoc !== "_none_" && (eq.location || "") !== filterLoc)
					return false;
			}
			if (filterStatus && (eq.status || "") !== filterStatus)
				return false;

			return true;
		});
	}, [
		equipment,
		listSearch,
		filterEmpID,
		filterVehID,
		filterLoc,
		filterStatus,
	]);

	const queueIds = useMemo(() => new Set(queue.map((q) => q.id)), [queue]);
	const allFilteredSelected =
		filteredEquip.length > 0 &&
		filteredEquip.every((eq) => queueIds.has(eq.id));

	const employeeOpts = useMemo(
		() =>
			employees
				.filter((e) => e.isActive)
				.map((e) => ({
					value: String(e.id),
					label: `${e.badge} — ${e.rank} ${e.firstName} ${e.lastName}`,
				})),
		[employees],
	);

	const vehicleOpts = useMemo(
		() =>
			vehicles
				.filter((v) => v.status !== "Retired")
				.map((v) => ({
					value: String(v.id),
					label: `${v.unitNumber} — ${v.year} ${v.make} ${v.model}`,
				})),
		[vehicles],
	);

	// ── effects ────────────────────────────────────────────────────────────
	useEffect(() => {
		if (!open) {
			setStep(0);
			setMode("list");
			setQueue([]);
			setAssignStatus("");
			setEmpMode("keep");
			setVehMode("keep");
			setLocMode("keep");
			setAssign({ employeeID: "", vehicleID: "", location: "" });
			setListSearch("");
			setFilterEmpID("");
			setFilterVehID("");
			setFilterLoc("");
			setFilterStatus("");
			setScanInput("");
			setScanMsg({ text: "", ok: false });
			stopCamera();
			setConfirmClose(false);
		}
	}, [open]);

	useEffect(() => () => stopCamera(), []);

	// ── camera helpers ─────────────────────────────────────────────────────
	const stopCamera = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		setScannerActive(false);
	};

	const startCamera = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment" },
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				videoRef.current.play();
			}
			setScannerActive(true);
			setScanMsg({
				text: "Camera active — position barcode in frame",
				ok: true,
			});
		} catch {
			setScanMsg({
				text: "Camera unavailable. Enter barcode manually below.",
				ok: false,
			});
		}
	};

	// ── queue helpers ──────────────────────────────────────────────────────
	const toggleItem = (eq) =>
		setQueue((q) =>
			queueIds.has(eq.id) ? q.filter((x) => x.id !== eq.id) : [...q, eq],
		);

	const removeFromQueue = (id) =>
		setQueue((q) => q.filter((x) => x.id !== id));

	const toggleAll = () => {
		if (allFilteredSelected) {
			setQueue((q) =>
				q.filter((x) => !filteredEquip.some((f) => f.id === x.id)),
			);
		} else {
			const toAdd = filteredEquip.filter((eq) => !queueIds.has(eq.id));
			setQueue((q) => [...q, ...toAdd]);
		}
	};

	// ── scan handler ───────────────────────────────────────────────────────
	const handleScan = (val) => {
		const v = val.trim();
		if (!v) return;

		const found = equipment.find(
			(eq) =>
				eq.serialNumber === v ||
				eq.assetTag === v ||
				eq.propertyNumber === v,
		);

		if (found) {
			if (queueIds.has(found.id)) {
				setScanMsg({
					text: `Already queued: ${found.type} ${found.serialNumber || found.assetTag}`,
					ok: false,
				});
			} else {
				setQueue((q) => [...q, found]);
				setScanMsg({
					text: `✓ Added: ${found.type} — ${[found.brand, found.model].filter(Boolean).join(" ")} (${found.serialNumber || found.assetTag || found.propertyNumber})`,
					ok: true,
				});
			}
		} else {
			setScanMsg({
				text: `No match for "${v}". Try serial #, asset tag, or property #.`,
				ok: false,
			});
		}

		setScanInput("");
		scanInputRef.current?.focus();
	};

	// ── save / close ───────────────────────────────────────────────────────
	const handleSave = () => {
		if (!queue.length) return;

		onSave(
			queue.map((eq) => eq.id),
			{
				empMode,
				employeeID:
					empMode === "assign" ? +assign.employeeID || null : null,
				vehMode,
				vehicleID:
					vehMode === "assign" ? +assign.vehicleID || null : null,
				locMode,
				location: locMode === "set" ? assign.location : null,
				status: assignStatus || null,
			},
		);
		onClose();
	};

	const handleClose = () => {
		if (queue.length > 0) {
			setConfirmClose(true);
		} else {
			onClose();
		}
	};

	// ── queue chips sub-component ──────────────────────────────────────────
	const QueueChips = ({ removable }) => (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
			{queue.map((eq) => (
				<Chip
					key={eq.id}
					size="small"
					label={`${eq.type}  ${eq.serialNumber || eq.assetTag || eq.propertyNumber || `#${eq.id}`}`}
					onDelete={
						removable ? () => removeFromQueue(eq.id) : undefined
					}
					variant="outlined"
				/>
			))}
		</Box>
	);

	// ── clear-filters visible? ─────────────────────────────────────────────
	const hasFilters = filterEmpID || filterVehID || filterLoc || filterStatus;

	// ────────────────────────────────────────────────────────────────────────
	return (
		<>
			{/* ── main dialog ── */}
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
				<DialogTitle>⚡ Quick Assign Equipment</DialogTitle>

				{/* stepper (same position as Tabs in project dialogs) */}
				<Box
					sx={{
						borderBottom: 1,
						borderColor: "divider",
						px: 3,
						pt: 1,
						pb: 2,
					}}
				>
					<Stepper activeStep={step}>
						<Step>
							<StepLabel>Select Items</StepLabel>
						</Step>
						<Step>
							<StepLabel>Set Destination</StepLabel>
						</Step>
					</Stepper>
				</Box>

				<DialogContent>
					{/* ══════════ STEP 1 — Select Items ══════════ */}
					{step === 0 && (
						<Box sx={{ pt: 1 }}>
							{/* mode toggle */}
							<ToggleButtonGroup
								value={mode}
								exclusive
								onChange={(_, v) => {
									if (v) {
										setMode(v);
										if (v === "list") stopCamera();
									}
								}}
								size="small"
								sx={{ mb: 2 }}
							>
								<ToggleButton value="list">
									📋 From List
								</ToggleButton>
								<ToggleButton value="scan">
									📷 Scan Barcodes
								</ToggleButton>
							</ToggleButtonGroup>

							{/* ── list mode ── */}
							{mode === "list" && (
								<Box>
									<TextField
										fullWidth
										size="small"
										label="Search serial #, asset tag, type, brand…"
										value={listSearch}
										onChange={(e) =>
											setListSearch(e.target.value)
										}
										autoFocus
										sx={{ mb: 1 }}
									/>

									{/* filter row */}
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns:
												"repeat(4, 1fr)",
											gap: 1,
											mb: 1.5,
										}}
									>
										<FormControl size="small" fullWidth>
											<InputLabel>Employee</InputLabel>
											<Select
												value={filterEmpID}
												onChange={(e) =>
													setFilterEmpID(
														e.target.value,
													)
												}
												label="Employee"
											>
												<MenuItem value="">
													Any Employee
												</MenuItem>
												<MenuItem value="0">
													— Unassigned —
												</MenuItem>
												{employees
													.filter((e) => e.isActive)
													.map((e) => (
														<MenuItem
															key={e.id}
															value={String(e.id)}
														>
															{e.badge}{" "}
															{e.lastName},{" "}
															{e.firstName}
														</MenuItem>
													))}
											</Select>
										</FormControl>

										<FormControl size="small" fullWidth>
											<InputLabel>Vehicle</InputLabel>
											<Select
												value={filterVehID}
												onChange={(e) =>
													setFilterVehID(
														e.target.value,
													)
												}
												label="Vehicle"
											>
												<MenuItem value="">
													Any Vehicle
												</MenuItem>
												<MenuItem value="0">
													— Unassigned —
												</MenuItem>
												{vehicles
													.filter(
														(v) =>
															v.status !==
															"Retired",
													)
													.map((v) => (
														<MenuItem
															key={v.id}
															value={String(v.id)}
														>
															{v.unitNumber}{" "}
															{v.year} {v.make}
														</MenuItem>
													))}
											</Select>
										</FormControl>

										<FormControl size="small" fullWidth>
											<InputLabel>Location</InputLabel>
											<Select
												value={filterLoc}
												onChange={(e) =>
													setFilterLoc(e.target.value)
												}
												label="Location"
											>
												<MenuItem value="">
													Any Location
												</MenuItem>
												<MenuItem value="_none_">
													— No Location —
												</MenuItem>
												{uniqueLocations.map((loc) => (
													<MenuItem
														key={loc}
														value={loc}
													>
														{loc}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl size="small" fullWidth>
											<InputLabel>Status</InputLabel>
											<Select
												value={filterStatus}
												onChange={(e) =>
													setFilterStatus(
														e.target.value,
													)
												}
												label="Status"
											>
												<MenuItem value="">
													Any Status
												</MenuItem>
												{activeCodes(
													"equipmentStatuses",
													codes,
												).map((s) => (
													<MenuItem key={s} value={s}>
														{s}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>

									{hasFilters && (
										<Box
											sx={{
												display: "flex",
												justifyContent: "flex-end",
												mb: 1,
											}}
										>
											<Button
												size="small"
												onClick={() => {
													setFilterEmpID("");
													setFilterVehID("");
													setFilterLoc("");
													setFilterStatus("");
												}}
											>
												✕ Clear filters
											</Button>
										</Box>
									)}

									{/* select-all */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											mb: 0.5,
										}}
									>
										<Box
											component="label"
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												cursor: "pointer",
												userSelect: "none",
											}}
										>
											<Checkbox
												size="small"
												checked={allFilteredSelected}
												onChange={toggleAll}
											/>
											<Typography variant="caption">
												{allFilteredSelected
													? "Deselect"
													: "Select"}{" "}
												all
												{filteredEquip.length > 0
													? ` (${filteredEquip.length})`
													: ""}
											</Typography>
										</Box>
									</Box>

									{/* equipment list */}
									<Box
										sx={{
											maxHeight: 300,
											overflowY: "auto",
											border: 1,
											borderColor: "divider",
											borderRadius: 1,
										}}
									>
										{filteredEquip.length === 0 ? (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													textAlign: "center",
													py: 3,
												}}
											>
												No equipment found
											</Typography>
										) : (
											filteredEquip.map((eq) => {
												const checked = queueIds.has(
													eq.id,
												);
												const assignedEmp =
													employees.find(
														(e) =>
															e.id ===
															eq.assignedOfficerID,
													);
												const assignedVeh =
													vehicles.find(
														(v) =>
															v.id ===
															eq.assignedVehicleID,
													);

												return (
													<Box
														key={eq.id}
														onClick={() =>
															toggleItem(eq)
														}
														sx={{
															p: "9px 14px",
															cursor: "pointer",
															borderBottom: 1,
															borderColor:
																"divider",
															display: "flex",
															alignItems:
																"center",
															gap: 1.5,
															bgcolor: checked
																? "action.selected"
																: "transparent",
															borderLeft:
																"3px solid",
															borderLeftColor:
																checked
																	? "primary.main"
																	: "transparent",
															"&:hover": {
																bgcolor: checked
																	? "action.selected"
																	: "action.hover",
															},
														}}
													>
														<Checkbox
															size="small"
															checked={checked}
															tabIndex={-1}
															disableRipple
															sx={{
																p: 0,
																pointerEvents:
																	"none",
															}}
														/>

														<Box
															sx={{
																flex: 1,
																minWidth: 0,
															}}
														>
															<Typography
																variant="body2"
																fontWeight={600}
															>
																{eq.type} —{" "}
																{[
																	eq.brand,
																	eq.model,
																]
																	.filter(
																		Boolean,
																	)
																	.join(" ")}
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
																fontFamily="monospace"
																display="block"
															>
																{
																	eq.serialNumber
																}
																{eq.propertyNumber
																	? ` · ${eq.propertyNumber}`
																	: ""}
																{eq.assetTag
																	? ` · ${eq.assetTag}`
																	: ""}
															</Typography>
															<Typography
																variant="caption"
																color="text.disabled"
																display="block"
															>
																{assignedEmp
																	? `👤 ${assignedEmp.badge} ${assignedEmp.lastName}`
																	: ""}
																{assignedEmp &&
																assignedVeh
																	? " · "
																	: ""}
																{assignedVeh
																	? `🚓 ${assignedVeh.unitNumber}`
																	: ""}
																{!assignedEmp &&
																	!assignedVeh &&
																	(eq.location ||
																		"Unassigned")}
															</Typography>
														</Box>

														<Chip
															size="small"
															label={
																eq.status || "—"
															}
															variant="outlined"
														/>
													</Box>
												);
											})
										)}
									</Box>
								</Box>
							)}

							{/* ── scan mode ── */}
							{mode === "scan" && (
								<Box>
									<Box
										sx={{
											position: "relative",
											width: "100%",
											aspectRatio: "16/9",
											bgcolor: "#000",
											borderRadius: 1,
											overflow: "hidden",
											mb: 1.5,
										}}
									>
										<video
											ref={videoRef}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
											muted
											playsInline
										/>
										<Box
											sx={{
												position: "absolute",
												inset: 0,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												pointerEvents: "none",
											}}
										>
											<Box
												sx={{
													width: "60%",
													height: "35%",
													border: "2px solid rgba(245,166,35,0.8)",
													borderRadius: 1,
													boxShadow:
														"0 0 0 9999px rgba(0,0,0,0.4)",
												}}
											/>
										</Box>

										{!scannerActive && (
											<Box
												sx={{
													position: "absolute",
													inset: 0,
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													justifyContent: "center",
													gap: 1,
												}}
											>
												<Button
													variant="contained"
													onClick={startCamera}
												>
													📷 Start Camera
												</Button>
												<Typography
													variant="caption"
													sx={{
														color: "rgba(255,255,255,0.6)",
													}}
												>
													or enter barcode manually
													below
												</Typography>
											</Box>
										)}
									</Box>

									{scanMsg.text && (
										<Alert
											severity={
												scanMsg.ok
													? "success"
													: "warning"
											}
											sx={{ mb: 1 }}
										>
											{scanMsg.text}
										</Alert>
									)}

									<Box
										sx={{
											display: "flex",
											gap: 1,
											mb: 0.5,
										}}
									>
										<TextField
											inputRef={scanInputRef}
											fullWidth
											size="small"
											label="Scan or type serial #, asset tag, property # then press Enter"
											value={scanInput}
											onChange={(e) => {
												setScanInput(e.target.value);
												setScanMsg({
													text: "",
													ok: false,
												});
											}}
											onKeyDown={(e) =>
												e.key === "Enter" &&
												handleScan(scanInput)
											}
											autoFocus
											InputProps={{
												sx: {
													fontFamily: "monospace",
												},
											}}
										/>
										<Button
											variant="contained"
											onClick={() =>
												handleScan(scanInput)
											}
										>
											Add
										</Button>
									</Box>

									<Typography
										variant="caption"
										color="text.secondary"
									>
										Each scan adds to the queue — scan as
										many as needed before assigning.
									</Typography>
								</Box>
							)}

							{/* queue preview */}
							{queue.length > 0 && (
								<Box
									sx={{
										mt: 2,
										p: 2,
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
										bgcolor: "action.hover",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
										>
											🗂 Queue — {queue.length} item
											{queue.length !== 1 ? "s" : ""}{" "}
											selected
										</Typography>
										<Button
											size="small"
											onClick={() => setQueue([])}
										>
											Clear all
										</Button>
									</Box>
									<QueueChips removable />
								</Box>
							)}
						</Box>
					)}

					{/* ══════════ STEP 2 — Set Destination ══════════ */}
					{step === 1 && (
						<Box sx={{ pt: 1 }}>
							{/* queue summary */}
							<Box
								sx={{
									p: 2,
									border: 2,
									borderColor: "primary.main",
									borderRadius: 1,
									mb: 3,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Assigning {queue.length} item
									{queue.length !== 1 ? "s" : ""} — click ✕ to
									remove before saving
								</Typography>
								<QueueChips removable />
							</Box>

							{/* assignment form */}
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "1fr",
									gap: 2.5,
								}}
							>
								{/* status */}
								<Box>
									<Typography
										variant="body2"
										fontWeight={600}
										sx={{ mb: 0.5 }}
									>
										Status
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											mb: 1,
											display: "block",
										}}
									>
										Leave blank to keep each item's current
										status
									</Typography>
									<FormControl
										label="Status"
										size="small"
										fullWidth
									>
										<Select
											value={assignStatus}
											onChange={(e) =>
												setAssignStatus(e.target.value)
											}
											label="Status"
											displayEmpty
										>
											{activeCodes(
												"equipmentStatuses",
												codes,
											).map((s) => (
												<MenuItem key={s} value={s}>
													{s}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>

								{/* employee */}
								<Box>
									<Typography
										variant="body2"
										fontWeight={600}
										sx={{ mb: 1 }}
									>
										Assigned Employee
									</Typography>
									<ToggleButtonGroup
										value={empMode}
										exclusive
										onChange={(_, v) => v && setEmpMode(v)}
										size="small"
										fullWidth
										sx={{
											mb: empMode === "assign" ? 1 : 0,
										}}
									>
										<ToggleButton value="keep">
											🔒 Keep
										</ToggleButton>
										<ToggleButton value="unassign">
											❌ Unassign
										</ToggleButton>
										<ToggleButton value="assign">
											👤 Assign to…
										</ToggleButton>
									</ToggleButtonGroup>

									{empMode === "assign" && (
										<Autocomplete
											size="small"
											options={employeeOpts}
											getOptionLabel={(o) =>
												typeof o === "string"
													? o
													: o.label
											}
											value={
												employeeOpts.find(
													(o) =>
														o.value ===
														assign.employeeID,
												) || null
											}
											onChange={(_, v) =>
												setAssign((a) => ({
													...a,
													employeeID: v?.value || "",
												}))
											}
											isOptionEqualToValue={(o, v) =>
												o.value === v.value
											}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Select employee…"
												/>
											)}
										/>
									)}
								</Box>

								{/* vehicle */}
								<Box>
									<Typography
										variant="body2"
										fontWeight={600}
										sx={{ mb: 1 }}
									>
										Assigned Vehicle
									</Typography>
									<ToggleButtonGroup
										value={vehMode}
										exclusive
										onChange={(_, v) => v && setVehMode(v)}
										size="small"
										fullWidth
										sx={{
											mb: vehMode === "assign" ? 1 : 0,
										}}
									>
										<ToggleButton value="keep">
											🔒 Keep
										</ToggleButton>
										<ToggleButton value="unassign">
											❌ Unassign
										</ToggleButton>
										<ToggleButton value="assign">
											🚓 Assign to…
										</ToggleButton>
									</ToggleButtonGroup>

									{vehMode === "assign" && (
										<Autocomplete
											size="small"
											options={vehicleOpts}
											getOptionLabel={(o) =>
												typeof o === "string"
													? o
													: o.label
											}
											value={
												vehicleOpts.find(
													(o) =>
														o.value ===
														assign.vehicleID,
												) || null
											}
											onChange={(_, v) =>
												setAssign((a) => ({
													...a,
													vehicleID: v?.value || "",
												}))
											}
											isOptionEqualToValue={(o, v) =>
												o.value === v.value
											}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Select vehicle…"
												/>
											)}
										/>
									)}
								</Box>

								{/* location */}
								<Box>
									<Typography
										variant="body2"
										fontWeight={600}
										sx={{ mb: 1 }}
									>
										Location
									</Typography>
									<ToggleButtonGroup
										value={locMode}
										exclusive
										onChange={(_, v) => v && setLocMode(v)}
										size="small"
										fullWidth
										sx={{
											mb: locMode === "set" ? 1 : 0,
										}}
									>
										<ToggleButton value="keep">
											🔒 Keep
										</ToggleButton>
										<ToggleButton value="clear">
											❌ Clear
										</ToggleButton>
										<ToggleButton value="set">
											📍 Set…
										</ToggleButton>
									</ToggleButtonGroup>

									{locMode === "set" && (
										<TextField
											fullWidth
											size="small"
											label="e.g. Armory, Storage Bay 3, Locker 42…"
											value={assign.location}
											onChange={(e) =>
												setAssign((a) => ({
													...a,
													location: e.target.value,
												}))
											}
										/>
									)}
								</Box>
							</Box>

							{/* change summary */}
							{(empMode !== "keep" ||
								vehMode !== "keep" ||
								locMode !== "keep" ||
								assignStatus) && (
								<Box
									sx={{
										mt: 2,
										p: 2,
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
										bgcolor: "action.hover",
									}}
								>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1,
											display: "block",
										}}
									>
										Changes applied to all {queue.length}{" "}
										items
									</Typography>

									<Box
										sx={{
											display: "flex",
											gap: 2,
											flexWrap: "wrap",
										}}
									>
										{assignStatus && (
											<Typography variant="body2">
												📋 Status → {assignStatus}
											</Typography>
										)}

										{empMode === "assign" &&
											(() => {
												const e = employees.find(
													(emp) =>
														emp.id ===
														+assign.employeeID,
												);
												return e ? (
													<Typography variant="body2">
														👤 {e.badge} — {e.rank}{" "}
														{e.firstName}{" "}
														{e.lastName}
													</Typography>
												) : null;
											})()}
										{empMode === "unassign" && (
											<Typography variant="body2">
												👤 Unassigned (clear employee)
											</Typography>
										)}
										{empMode === "keep" && (
											<Typography
												variant="body2"
												color="text.disabled"
											>
												👤 Employee unchanged
											</Typography>
										)}

										{vehMode === "assign" &&
											(() => {
												const v = vehicles.find(
													(veh) =>
														veh.id ===
														+assign.vehicleID,
												);
												return v ? (
													<Typography variant="body2">
														🚓 {v.unitNumber} —{" "}
														{v.year} {v.make}{" "}
														{v.model}
													</Typography>
												) : null;
											})()}
										{vehMode === "unassign" && (
											<Typography variant="body2">
												🚓 Unassigned (clear vehicle)
											</Typography>
										)}
										{vehMode === "keep" && (
											<Typography
												variant="body2"
												color="text.disabled"
											>
												🚓 Vehicle unchanged
											</Typography>
										)}

										{locMode === "set" &&
											assign.location && (
												<Typography variant="body2">
													📍 {assign.location}
												</Typography>
											)}
										{locMode === "clear" && (
											<Typography variant="body2">
												📍 Location cleared
											</Typography>
										)}
										{locMode === "keep" && (
											<Typography
												variant="body2"
												color="text.disabled"
											>
												📍 Location unchanged
											</Typography>
										)}
									</Box>
								</Box>
							)}
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2 }}>
					{step === 1 && (
						<Button onClick={() => setStep(0)}>← Back</Button>
					)}
					<Box sx={{ flex: 1 }} />
					{step === 0 && (
						<Button
							variant="contained"
							disabled={queue.length === 0}
							onClick={() => setStep(1)}
						>
							Next → ({queue.length} item
							{queue.length !== 1 ? "s" : ""})
						</Button>
					)}
					{step === 1 && (
						<PATSaveButton
							onClick={handleSave}
							tooltipText={`Assign ${queue.length} Item${queue.length !== 1 ? "s" : ""}`}
						/>
					)}
					<PATCloseButton
						onClick={handleClose}
						tooltipText="Cancel"
					/>
				</DialogActions>
			</Dialog>

			{/* ── confirm-discard dialog ── */}
			<Dialog
				open={confirmClose}
				onClose={() => setConfirmClose(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>⚠️ Discard selection?</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary">
						You have {queue.length} item
						{queue.length !== 1 ? "s" : ""} selected. Closing will
						discard this queue.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setConfirmClose(false)}>
						Keep editing
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={() => {
							setConfirmClose(false);
							onClose();
						}}
					>
						Discard &amp; close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default QuickAssignDialog;
